import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import ArrowRight from '@/assets/icons/arrow-right.svg?react'
import { Button, message, Modal, Trans } from '@/components'
import { ClusterInstance } from '@/models/mesh'
import { Network } from '@/models/network'
import { cn } from '@/utils/style'

import ClusterInstanceSourceTable from './ClusterInstanceSourceTable'
import ClusterInstanceTargetTable from './ClusterInstanceTargetTable'

interface ClusterAddInstanceModalProps {
  network: Network
  regionDisplayName: string
  onSuccess: () => void
  onClose: () => void
}

export default function ClusterAddInstanceModal({
  network,
  regionDisplayName,
  onSuccess = () => {},
  onClose = () => {},
}: ClusterAddInstanceModalProps) {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const [sourceInstances, setSourceInstances] = useState<ClusterInstance[]>([])

  const [targetKeys, setTargetKeys] = useState<React.Key[]>([])
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])

  const onSourceUpdated = (rows: ClusterInstance[]) => {
    // console.log('[onSourceUpdated]', rows)
    setSourceInstances(rows)
  }

  const onSelectedRowsChange = (rowKeys: React.Key[]) => {
    // console.log('[onSelectedRowsChange]', rowKeys)
    setSelectedKeys(rowKeys)
  }

  const onTransfor = () => {
    const newTargetKeys = [...targetKeys, ...selectedKeys]
    setTargetKeys(newTargetKeys)

    setSourceInstances((prev) =>
      prev.map((instance) =>
        selectedKeys.includes(instance.resID) ? { ...instance, disabled: true } : instance,
      ),
    )

    setSelectedKeys([])
  }

  const onRemoveSelectedFromTarget = (removeKeys: React.Key[]) => {
    const newTargetKeys = targetKeys.filter((key) => !removeKeys.includes(key))
    setTargetKeys(newTargetKeys)

    setSourceInstances((prev) =>
      prev.map((instance) =>
        removeKeys.includes(instance.resID) ? { ...instance, disabled: false } : instance,
      ),
    )

    const newSelectedKeys = selectedKeys.filter((key) => !removeKeys.includes(key))
    setSelectedKeys(newSelectedKeys)
  }

  const onOK = () => {
    setLoading(true)
    API.addEndpointToMesh({
      res_id: network.resID,
      inst_res_ids: [...((targetKeys || []) as any)],
    })
      .then((res) => {
        if (res.code === 0) {
          message.success(t('common.message.success'))
          onSuccess()
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Modal
      className="w-[1264px]"
      open={true}
      onClose={() => {
        if (!loading) {
          onClose()
        }
      }}
    >
      <Modal.Header>
        <Trans i18nKey="mesh.clusterAddInsModal.title">Add Instance</Trans>
      </Modal.Header>
      <Modal.Body>
        <div className="flex items-center justify-center">
          <div className="flex-grow w-[604px]">
            <ClusterInstanceSourceTable
              network={network}
              regionDisplayName={regionDisplayName}
              targetKeys={targetKeys}
              selectedKeys={selectedKeys}
              onSourceUpdated={onSourceUpdated}
              onSelectedRowsChange={onSelectedRowsChange}
            />
          </div>
          <div className="shrink-0 m-4">
            <div
              className={cn(
                'border border-neutral-200 rounded-md w-10 h-10 flex items-center justify-center t-sm select-none',
                {
                  'text-tertiary cursor-not-allowed': selectedKeys.length === 0,
                  'bg-btn-secondary text-white cursor-pointer': selectedKeys.length > 0,
                },
              )}
              onClick={() => {
                if (selectedKeys.length > 0) {
                  onTransfor()
                }
              }}
            >
              <ArrowRight className="" />
            </div>
          </div>
          <div className="flex-grow w-[524px]">
            <ClusterInstanceTargetTable
              network={network}
              sourceInstances={sourceInstances}
              onRemoveSelectedFromTarget={onRemoveSelectedFromTarget}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="outline"
          onClick={() => {
            if (!loading) {
              onClose()
            }
          }}
        >
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          disabled={loading || (targetKeys?.length ?? 0) <= 0}
          onClick={onOK}
        >
          <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
